import {
  GROUP_ORDER_SUBSIDY_TYPE_FIXED,
  GROUP_ORDER_SUBSIDY_TYPE_PERCENT,
  GROUP_ORDER_SUBSIDY_TYPE_REVERSE,
  GROUP_ORDER_SUBSIDY_TYPE_HUNGRY_WALLET,
  GROUP_ORDER_SUBSIDY_TYPE_FLAT_PRICE,
} from '../constants'
import {
  Availability,
  VendorGroup,
  PreviewMenu,
  PreviewMenuItem,
  UniversalMenuExplorerMenu,
  UniversalMenuExplorerMenuItem,
  UniversalMenuExplorerFilters,
  UniversalMenuConfig,
  DietaryTagKey,
  MenuItemMod,
  MenuItemModGroup,
  PreviewMenuItemModGroup,
  PreviewMenuItemMod,
} from '@types'
import { parseMilitaryTimeToAMPM } from '@utils'

type CSVRow = Record<string, string | number | boolean>

export const vendorGroupsToCSV = (vendorGroups: VendorGroup[]): CSVRow[] => {
  return vendorGroups.flatMap((vg) => menusToCSV(vg.menus))
}

const menusToCSV = (menus: PreviewMenu[]): CSVRow[] => {
  if (!menus || menus.length == 0) {
    return []
  }
  const filteredMenus = menus.filter((menu) => menu.availability.available)

  return filteredMenus.flatMap((menu) => menuToCSVRow(menu))
}

const menuToCSVRow = (menu: PreviewMenu): CSVRow[] => {
  const filteredItems = menu.allMenuItems.filter(
    (item) => item.availability.available,
  )

  return filteredItems.map((item) => itemToCSVRow(item, menu))
}

const itemToCSVRow = (item: PreviewMenuItem, menu: PreviewMenu): CSVRow => {
  let earliestPickup = 'Not Set'
  let maxItemsPerDay = 'Not Set'
  if (menu.chef.chefProfile.groupOrderEarliestPickupTime) {
    earliestPickup = parseMilitaryTimeToAMPM(
      menu.chef.chefProfile.groupOrderEarliestPickupTime,
    )
  }
  if (menu.chef.chefProfile.maxGroupOrderItems) {
    maxItemsPerDay = menu.chef.chefProfile.maxGroupOrderItems.toString()
  }

  return {
    'Vendor Name': menu.chef.fullName,
    'Vendor Earliest Pickup': earliestPickup,
    'Vendor Max Items Per Day': maxItemsPerDay,
    'Menu Name': menu.name,
    'Is Menu Universal': menu.isUniversal ? 'Yes' : 'No',
    'Menu Avg Margin': `${menu.margin.toFixed(2)}%`,
    'Item Name': item.name,
    'Meal Type': item.mealType,
    'Has Mods': item.hasMods ? 'Yes' : 'No',
    'Customizable Alternative Name': item.customizedAlternative
      ? item.customizedAlternative
      : 'N/A',
    'Dietary Tags': item.tagsDietaryPreferenceList,
    'Vendor Price': `$${item.chefPrice.toFixed(2)}`,
    'Client Price': `$${item.price.toFixed(2)}`,
    Margin: `${item.margin.toFixed(2)}%`,
    'Subsidy Covered': `$${item.subsidyCovered.toFixed(2)}`,
    'Employee Pays': `$${item.employeePays.toFixed(2)}`,
  }
}

type SubsidyBreakdown = {
  subsidyCovered: number
  employeePays: number
}

const clamp = (n: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, n))

export const calcSubsidyBreakdown = (
  clientPrice: number,
  filters: UniversalMenuExplorerFilters,
): SubsidyBreakdown => {
  const price = Math.max(0, Number(clientPrice) || 0)
  const t = filters.subsidyType
  const amt = filters.subsidyAmt ?? 0

  let subsidyCovered = 0
  let employeePays = price
  switch (t) {
    case GROUP_ORDER_SUBSIDY_TYPE_HUNGRY_WALLET:
    case GROUP_ORDER_SUBSIDY_TYPE_FIXED: {
      subsidyCovered = clamp(amt, 0, price)
      employeePays = price - subsidyCovered
      break
    }
    case GROUP_ORDER_SUBSIDY_TYPE_PERCENT: {
      const pct = (clamp(amt, 0, 100) / 100).toFixed(2)
      subsidyCovered = Number((price * Number(pct)).toFixed(2))
      employeePays = price - subsidyCovered
      break
    }
    case GROUP_ORDER_SUBSIDY_TYPE_FLAT_PRICE:
    case GROUP_ORDER_SUBSIDY_TYPE_REVERSE: {
      const flat = clamp(amt, 0, price)
      employeePays = flat
      subsidyCovered = price - flat
      break
    }
    default: {
      subsidyCovered = 0
      employeePays = price
    }
  }

  return {
    subsidyCovered,
    employeePays,
  }
}

export const disabledClasses = (
  disabled: boolean,
  base: string,
  whenDisabled: string,
) => (disabled ? `${base} ${whenDisabled}` : base)

const normalizeText = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim()

export const menuOrItemsMatchSearch = (
  chefName: string,
  menuName: string,
  items: Array<{ name: string }>,
  search: string,
): boolean => {
  const normalizedSearch = normalizeText(search)
  if (!normalizedSearch) {
    return true
  }
  if (normalizeText(chefName).includes(normalizedSearch)) {
    return true
  }
  if (normalizeText(menuName).includes(normalizedSearch)) {
    return true
  }
  for (const it of items) {
    if (normalizeText(it.name).includes(normalizedSearch)) {
      return true
    }
  }

  return false
}

export const decorateItem = (
  it: UniversalMenuExplorerMenuItem,
  f: UniversalMenuExplorerFilters,
): Omit<PreviewMenuItem, 'availability'> => {
  const clientPrice = it.price
  const { subsidyCovered, employeePays } = calcSubsidyBreakdown(it.price, f)
  const margin = Number(
    (((clientPrice - it.chefPrice) / clientPrice) * 100).toFixed(2),
  )
  const decoratedGroups: PreviewMenuItemModGroup[] = it.availableModGroups.map(
    (g) => computeModGroupAvailability(g, f, it),
  )

  return {
    ...it,
    subsidyCovered,
    employeePays,
    margin,
    availableModGroups: decoratedGroups,
  }
}

export const computeMenuAvailability = (
  menu: UniversalMenuExplorerMenu,
  f: UniversalMenuExplorerFilters,
  blacklistedVendorIds: Set<string>,
): Availability => {
  const reasons: string[] = []

  if (menu.chef?.id && blacklistedVendorIds.has(menu.chef.id)) {
    reasons.push('Vendor blacklisted (universal settings)')
  }

  return { available: reasons.length === 0, reasons }
}

export const computeItemAvailbility = (
  item:
    | UniversalMenuExplorerMenuItem
    | (UniversalMenuExplorerMenuItem & {
        availableModGroups: PreviewMenuItemModGroup[]
      }),
  blacklistedItemIds: Set<string>,
  filters: UniversalMenuExplorerFilters,
): Availability => {
  const reasons = []
  if (item.id && blacklistedItemIds.has(item.id)) {
    reasons.push('Item blacklisted')
  }
  if (!filters.enableSides && item.isSide) {
    reasons.push('Sides disabled')
  }
  if (!filters.enableMods && item.hasMods) {
    reasons.push('Modifications Disabled')
  }
  if (filters.enableMods && item.customizedAlternative) {
    reasons.push('Modifications enabled - excludes composed items')
  }
  if (filters.maxPriceLimit != null && item.price > filters.maxPriceLimit) {
    reasons.push('Item price exceeds max base item price limit')
  }
  if (
    filters.maxTotalItemPriceLimit != null &&
    item.price > filters.maxTotalItemPriceLimit
  ) {
    reasons.push('Item price exceeds max total item price limit')
  }
  if (
    filters.flatPriceMarginMin != null &&
    item.margin < filters.flatPriceMarginMin
  ) {
    reasons.push('Item margin is less than flat price margin min')
  }
  const keys = filters.excludedDietaryTagsKeys
  const matches: string[] = []
  for (const key of keys) {
    if (item.normalizedDietaryTags[key]) {
      const formatted = formatDietaryKey(key)
      matches.push(formatted)
    }
  }
  if (matches.length > 0) {
    reasons.push(`Excluded Dietary Tags found on item: ${matches.join(', ')}`)
  }
  if (filters.enableMods && item.availableModGroups.length > 0) {
    const requiredGroupsWithNoOptions = (
      item as { availableModGroups: PreviewMenuItemModGroup[] }
    ).availableModGroups
      .filter(
        (g) => g.isRequired && !g.mods.some((m) => m.availability.available),
      )
      .map((g) => g.name)
    if (requiredGroupsWithNoOptions.length > 0) {
      reasons.push(
        `Required mod groups missing available options: ${requiredGroupsWithNoOptions.join(
          ', ',
        )}`,
      )
    }
  }

  return { available: reasons.length === 0, reasons }
}

const formatDietaryKey = (key: DietaryTagKey): string => {
  const stripped = key.startsWith('contains')
    ? key.slice('contains'.length)
    : key.startsWith('is')
      ? key.slice('is'.length)
      : key

  return stripped
}

export const computeModAvailibility = (
  mod: MenuItemMod,
  filters: UniversalMenuExplorerFilters,
  it: UniversalMenuExplorerMenuItem,
): Availability => {
  const reasons: string[] = []
  if (mod.clientPrice !== null) {
    if (
      filters.maxModPriceLimit !== null &&
      mod.clientPrice > filters.maxModPriceLimit
    ) {
      reasons.push('Modification price exceeds max mod price')
    } else if (
      filters.maxTotalItemPriceLimit !== null &&
      it.price + mod.clientPrice > filters.maxTotalItemPriceLimit
    ) {
      reasons.push('Modification price would exceed max total item price limit')
    }
  }

  return { available: reasons.length === 0, reasons }
}

export const computeModGroupAvailability = (
  group: MenuItemModGroup,
  filters: UniversalMenuExplorerFilters,
  it: UniversalMenuExplorerMenuItem,
): PreviewMenuItemModGroup => {
  const decoratedMods: PreviewMenuItemMod[] = group.mods.map((m) => ({
    ...m,
    availability: computeModAvailibility(m, filters, it),
  }))
  const anyModAvailable = decoratedMods.some((m) => m.availability.available)
  const reasons: string[] = []
  if (!anyModAvailable) {
    reasons.push('No available modifications in group')
  }

  const out: PreviewMenuItemModGroup = {
    ...group,
    mods: decoratedMods,
    availability: { available: anyModAvailable, reasons },
  }

  return out
}

export const groupByVendor = (menus: PreviewMenu[]): VendorGroup[] => {
  const map = new Map<string, VendorGroup>()
  for (const m of menus) {
    const vid = m.chef?.id ?? 'unknown'
    const vname = m.chef?.fullName ?? 'Unknown Vendor'
    const maxItemsPerDay = m.chef.chefProfile.maxGroupOrderItems || undefined
    const earliestGroupOrderPickupTime =
      m.chef.chefProfile.groupOrderEarliestPickupTime || undefined
    const { minGOPayout } = m

    if (!map.has(vid)) {
      map.set(vid, {
        vendorId: vid,
        vendorName: vname,
        menus: [],
        minGOPayout,
        maxItemsPerDay: maxItemsPerDay,
        earliestPickup: earliestGroupOrderPickupTime,
      })
    }
    const vendorGroup = map.get(vid)
    if (vendorGroup) {
      vendorGroup.menus.push(m)
    }
  }

  return Array.from(map.values()).sort((a, b) =>
    a.vendorName.localeCompare(b.vendorName),
  )
}

export const toDietaryTagKey = (label: string): DietaryTagKey | null => {
  const trimmed = label.replace(/^contains\s+/i, '')
  const key = `contains${trimmed.charAt(0).toUpperCase()}${trimmed.slice(
    1,
  )}` as DietaryTagKey

  return key
}

export const buildFiltersFromConfig = (
  config: UniversalMenuConfig,
): UniversalMenuExplorerFilters => {
  const keys = config.excludedDietaryTags
    .map(toDietaryTagKey)
    .filter((k): k is DietaryTagKey => Boolean(k))

  const out: UniversalMenuExplorerFilters = {
    enableMods: config.enableModifications,
    enableSides: config.enableSides,
    excludedDietaryTagsKeys: keys,
    excludedDietaryTags: config.excludedDietaryTags,
    maxPriceLimit: config.maxItemPriceLimit,
    maxModPriceLimit: config.maxModificationPriceLimit,
    maxTotalItemPriceLimit: config.maxTotalItemPriceLimit,
    dayOfWeek: null,
    subsidyAmt: null,
    subsidyType: null,
    flatPriceMarginMin: config.flatPriceMarginMinimum,
  }

  return out
}
